<template>
  <v-card class="default-card pa-6" elevation="3" color="surface-light">
    <v-card-title class="toolbox-card-title">
      {{ $t('views.architect.dashboard.title') }}
    </v-card-title>
    <v-card-text>
      {{ $t('views.architect.dashboard.subtitle') }}
    </v-card-text>

    <v-tabs v-model="toolboxTab" color="secondary" class="mb-4" align-tabs="end">
      <v-tab value="tools">
        {{ $t('views.architect.dashboard.tabs.tools') }}
      </v-tab>
      <v-tab value="map">
        {{ $t('views.architect.dashboard.tabs.map') }}
      </v-tab>
    </v-tabs>

    <v-tabs-window v-model="toolboxTab">
      <v-tabs-window-item value="tools">
        <v-row align="stretch" justify="center" dense>
          <v-col
            v-for="area in areas"
            :key="area.id"
            cols="12"
            md="6"
            class="d-flex"
          >
            <v-hover v-slot="{ isHovering, props }">
              <v-card
                v-bind="props"
                class="architect-card pa-6"
                :class="{
                  'architect-card--disabled': area.disabled,
                  'architect-card--hover': isHovering && !area.disabled
                }"
                :elevation="isHovering && !area.disabled ? 10 : 3"
                role="button"
                :tabindex="area.disabled ? -1 : 0"
                :aria-disabled="area.disabled"
                @click="handleCardActivate(area)"
                @keydown.enter.prevent="handleCardActivate(area)"
                @keydown.space.prevent="handleCardActivate(area)"
              >
                <div class="architect-card__content">
                  <div class="architect-card__header">
                    <div class="architect-card__icon mr-3">
                      <v-img
                        v-if="area.iconSrc"
                        :alt="$t(area.titleKey)"
                        :src="area.iconSrc"
                        class="architect-card__icon-image"
                        contain
                      />
                      <v-icon v-else class="architect-card__icon-fallback" size="28">
                        {{ area.icon }}
                      </v-icon>
                    </div>
                    <div>
                      <h3 class="text-h6 mb-1">
                        {{ $t(area.titleKey) }}
                      </h3>
                    </div>
                  </div>
                  <div class="architect-card__body">
                    <p
                      v-for="(line, index) in area.details"
                      :key="index"
                      class="text-body-2 mb-1"
                    >
                      {{ line }}
                    </p>
                  </div>
                </div>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
      </v-tabs-window-item>
      <v-tabs-window-item value="map">
        <v-row>
          <v-col cols="12">
            <ProjectMap/>
          </v-col>
        </v-row>
      </v-tabs-window-item>
    </v-tabs-window>
  </v-card>

  <v-card v-if="hasBracingData" class="default-card pa-6 mt-6" elevation="3" color="surface-light">
    <v-card-title class="intervenant-card-title text-h5 mb-2">
      {{ $t('views.architect.dashboard.structureResults.title') }}
    </v-card-title>
    <v-tabs v-model="activeTab" color="secondary" class="mb-4" align-tabs="end">
      <v-tab value="structure">Structure</v-tab>
      <v-tab value="manufacturer">Manufacturer</v-tab>
    </v-tabs>
    <v-tabs-window v-model="activeTab">
      <v-tabs-window-item value="structure">
        <v-row align="stretch" justify="center" dense>
          <v-col cols="12" md="6" class="d-flex">
            <v-card class="architect-card" elevation="10">
              <div class="architect-card__content">
                <div class="architect-card__header">
                  <div class="architect-card__icon mr-3">
                    <v-icon class="architect-card__icon-fallback" size="28">mdi-crop-square</v-icon>
                  </div>
                  <div>
                    <h3 class="text-h6 mb-1">{{ $t('views.structure.functionalities.bracing.title') }}</h3>
                  </div>
                </div>
                <div class="architect-card__body">
                  <div v-for="(item, idx) in bracingInfo" :key="idx" class="text-body-2 mb-1">
                    {{ item.label }}: {{ item.value }}
                  </div>
                </div>
                <div class="architect-card__footer">
                  <v-chip color="success" size="small" variant="tonal">
                    {{ $t('views.structure.dashboard.status.available') }}
                  </v-chip>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6" class="d-flex">
            <v-card class="architect-card architect-card--disabled" elevation="3">
              <div class="architect-card__content">
                <div class="architect-card__header">
                  <div class="architect-card__icon mr-3">
                    <v-icon class="architect-card__icon-fallback" size="28">mdi-wall</v-icon>
                  </div>
                  <div>
                    <h3 class="text-h6 mb-1">{{ $t('views.structure.functionalities.bearingWall.title') }}</h3>
                  </div>
                </div>
                <div class="architect-card__body"></div>
                <div class="architect-card__footer">
                  <v-chip color="secondary" size="small" variant="tonal">
                    {{ $t('views.structure.dashboard.status.comingSoon') }}
                  </v-chip>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tabs-window-item>
      <v-tabs-window-item value="manufacturer">
        <p class="text-body-1 ma-4">À Venir</p>
      </v-tabs-window-item>
    </v-tabs-window>
  </v-card>
</template>

<script setup>
// TODOEB : the first part is similar to AreaSection, we should probably create a common component to avoid duplication
// TODOEB : The second part is similar to IntervenantResults, we should probably create a common component to avoid duplication
import { computed, ref, capitalize } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { architectAreas } from '@/constants/architectAreas';
import ProjectMap from '@/components/common/ProjectMap.vue';

const router = useRouter();
const store = useStore();
const activeTab = ref('structure');
const toolboxTab = ref('tools');
const { t } = useI18n();

const projectData = computed(() => store.state.projectData);
const formatUnitExponent = (value) => {
  return String(value)
    .replace(/<sup>2<\/sup>/gi, '²')
    .replace(/<sup>3<\/sup>/gi, '³')
    .replace(/<[^>]*>/g, '');
};

const materialSelectionDetails = () => {
  const panel = projectData.value?.panelCountEstimator || {};
  const config = panel.panelConfiguration || {};
  const estimation = panel.panelEstimation || {};
  const truck = estimation.truckDimensions || {};
  const selectedWalls = Array.isArray(panel.selectedWalls) ? panel.selectedWalls : [];
  const wall = selectedWalls[0] || {};
  return [
    `${t('functionalities.panelEstimator.wallType')}: ${panel.prefabricatedWallType || '-'}`,
    `${t('functionalities.panelEstimator.maxPanelSize')}: ${typeof config.maxPanelWidth === 'number' ? config.maxPanelWidth.toFixed(2) : '-'} x ${typeof config.maxPanelLength === 'number' ? config.maxPanelLength.toFixed(2) : '-'}`,
    `${t('functionalities.panelEstimator.selectedWallsArea')}: ${typeof wall.totalArea === 'number' ? wall.totalArea.toFixed(2) : '-'}`,
    `${t('functionalities.panelEstimator.selectedWallsLength')}: ${typeof wall.totalLength === 'number' ? wall.totalLength.toFixed(2) : '-'}`,
    `${t('functionalities.panelEstimator.truckDimensions')}: ${typeof truck.width === 'number' ? truck.width : '-'} x ${typeof truck.length === 'number' ? truck.length : '-'} x ${typeof truck.height === 'number' ? truck.height : '-'}`,
    `${t('functionalities.panelEstimator.spacerThickness')}: ${typeof estimation.spacerThickness === 'number' ? estimation.spacerThickness.toFixed(2) : '-'}`,
    `${t('functionalities.panelEstimator.fullPanels')}: ${typeof estimation.completePanels === 'number' ? estimation.completePanels.toFixed(2) : '-'}`,
    `${t('functionalities.panelEstimator.irregularPanels')}: ${typeof estimation.irregularPanels === 'number' ? estimation.irregularPanels.toFixed(2) : '-'}`,
    `${t('functionalities.panelEstimator.numberOfTrucks')}: ${typeof estimation.numberOfTrucks === 'number' ? estimation.numberOfTrucks.toFixed(2) : '-'}`
  ];
};

const carbonImpactEstimatorDetails = () => {
  const carbon = projectData.value?.carbonImpactEstimator?.materialtotals || {};
  const areaUnit = `${t('measuringUnits.inch')}${formatUnitExponent(t('square'))}`;
  const volumeUnit = `${t('measuringUnits.inch')}${formatUnitExponent(t('cube'))}`;
  const massUnit = t('measuringUnits.kg');
  const carbonUnit = t('measuringUnits.carbonDioxide');
  if (
    carbon.area == null &&
    carbon.volume == null &&
    carbon.mass == null &&
    carbon.carbonFootprint == null
  ) {
    return [t('functionalities.carbonImpactEstimator.description')];
  }
  return [
    `${t('functionalities.carbonImpactEstimator.area')}: ${typeof carbon.area === 'number' ? carbon.area.toFixed(2) : '-'} ${areaUnit}`,
    `${t('functionalities.carbonImpactEstimator.volume')}: ${typeof carbon.volume === 'number' ? carbon.volume.toFixed(2) : '-'} ${volumeUnit}`,
    `${t('functionalities.carbonImpactEstimator.mass')}: ${typeof carbon.mass === 'number' ? carbon.mass.toFixed(2) : '-'} ${massUnit}`,
    `${t('functionalities.carbonImpactEstimator.carbonFootprint')}: ${typeof carbon.carbonFootprint === 'number' ? carbon.carbonFootprint.toFixed(2) : '-'} ${carbonUnit}`
  ];
};
const projectInformationDetails = () => {
  const info = projectData.value?.projectInfo || {};
  const location = info.location || {};

  const addressParts = [
    [location.number, location.street].filter(Boolean).join(' ').trim(),
    location.city,
    location.province,
    location.postalCode,
    location.country,
  ].filter(Boolean);

  return [
    `${t('components.projectInfo.projectName')}: ${info.name || '-'}`,
    `${t('components.projectInfo.number')}: ${info.number || '-'}`,
    `${t('components.projectInfo.description')}: ${info.description || '-'}`,
    `${t('components.projectInfo.clientName')}: ${info.clientName || '-'}`,
    `${t('components.projectInfo.constructionStartDate')}: ${info.constructionStartDate || '-'}`,
    `${ capitalize(t('address')) }: ${addressParts.length ? addressParts.join(', ') : '-'}`,
  ];
};
const acousticPartitionAndSeparationDetails = () => [t('functionalities.acousticPartitionAndSeparation.description')];

const cardDataGenerators = {
  projectInformation: projectInformationDetails,
  materialSelection: materialSelectionDetails,
  acousticPartitionAndSeparation: acousticPartitionAndSeparationDetails,
  carbonImpactEstimator: carbonImpactEstimatorDetails,

};

const areas = computed(() => {
  return architectAreas.map((area) => {
    const generator = cardDataGenerators[area.id] ?? (() => [t(area.descriptionKey)]);

    return {
      ...area,
      details: generator()
    };
  });
});

const handleCardActivate = (area) => {
  if (area.disabled || !area.to) {
    return;
  }
  router.push(area.to);
};

const hasBracingData = computed(() => {
  const bracing = store.state.projectData?.bracing;
  return !!(bracing?.minimumLength || bracing?.validatedWalls?.length);
});

const bracingInfo = computed(() => {
  const bracing = store.state.projectData?.bracing ?? {};
  const validatedWalls = bracing.validatedWalls ?? [];
  const ewWalls = validatedWalls.filter(w => w.orientation !== 'X');
  const nsWalls = validatedWalls.filter(w => w.orientation === 'X');
  const ewTotalLength = ewWalls.reduce((sum, w) => sum + (w.length ?? 0), 0);
  const nsTotalLength = nsWalls.reduce((sum, w) => sum + (w.length ?? 0), 0);
  return [
    { label: t('views.structure.dashboard.bracing.necessaryLengthEastWest'), value: bracing.minimumLength?.eastWest ?? '-' },
    { label: t('views.structure.dashboard.bracing.necessaryLengthNorthSouth'), value: bracing.minimumLength?.northSouth ?? '-' },
    { label: t('views.structure.dashboard.bracing.validatedEastWestCount'), value: ewWalls.length },
    { label: t('views.structure.dashboard.bracing.validatedEastWestLength'), value: ewTotalLength },
    { label: t('views.structure.dashboard.bracing.validatedNorthSouthCount'), value: nsWalls.length },
    { label: t('views.structure.dashboard.bracing.validatedNorthSouthLength'), value: nsTotalLength },
    { label: t('views.structure.dashboard.bracing.missingLengthEastWest'), value: bracing.missingLength?.eastWest ?? '-' },
    { label: t('views.structure.dashboard.bracing.missingLengthNorthSouth'), value: bracing.missingLength?.northSouth ?? '-' },
  ];
});
</script>

<style scoped>
.toolbox-card-title {
  padding-bottom: 20px;
}

.architect-dashboard-card {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
}

.architect-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.architect-card--hover {
  transform: translateY(-4px);
}

.architect-card--disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background-color: var(--v-theme-surface-light);
}

.architect-card__content {
  height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
}

.architect-card__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.architect-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  background-color: rgba(0, 0, 0, 0.02);
}

.architect-card__icon-image {
  width: 100%;
  height: 100%;
}

.architect-card__icon-image :deep(.v-img__img) {
  object-fit: contain;
}

.architect-card__icon-fallback {
  color: var(--v-theme-secondary);
}

.architect-card__body {
  flex: 1;
}

.architect-card__footer {
  margin-top: 8px;
}

.intervenant-card-title {
  padding-bottom: 0px;
}

@media (max-width: 960px) {
  .architect-card__content {
    padding: 20px;
  }
}
</style>
